// v17 Đợt 1 (06/2026) — API Edge client (Cloudflare Worker `api-edge.pickleague.vn`).
//
// Bối cảnh: pickleague.vn routing qua lớp Cloudflare pass-through của Emergent
// → Cache Rules trên zone của mình KHÔNG chạy. Giải pháp: subdomain
// api-edge.pickleague.vn chạy Worker fetch origin Emergent + cache theo header
// `X-Edge-Cache-Policy` do backend phát.
//
// Quy tắc (HAR v16 fix — breaker v2):
//   • CHỈ dùng cho GET /api/public/* đã audit (không cookie, không auth).
//   • Abort (ERR_CANCELED / AbortError) KHÔNG phải lỗi edge: rethrow, không
//     mở breaker, không fallback, không retry.
//   • Breaker theo policyKey tường minh (KHÔNG raw URL chứa UUID/slug) —
//     endpoint A lỗi không kéo endpoint B về origin.
//   • 429 → tôn trọng, không mở breaker edge. 4xx data thật → trả nguyên.
//   • Network/timeout/5xx → mở breaker CHO policyKey đó (4-7' jitter).
//   • Breaker mở / edge fail → serve lastGood theo TTL từng loại dữ liệu;
//     hết stale: ESSENTIAL (nhẹ, thiết yếu) mới được fallback origin có
//     budget + single-flight; nhóm heavy/realtime throw isEdgeDegraded —
//     UI giữ khung, không đổ 500 viewer về origin.
//   • Preview không có EDGE_API_BASE → đi origin trực tiếp (PR-D nguyên trạng).
import axios from "axios";
import { API } from "./api";
import * as governor from "./networkGovernor";
import * as snapshots from "./offlineSnapshots";

const PROD_HOST = "pickleague.vn";
const PROD_EDGE = "https://api-edge.pickleague.vn";

function resolveEdgeBase() {
  const env = (process.env.REACT_APP_EDGE_API_URL || "").trim();
  if (env) return env.replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === PROD_HOST || h === `www.${PROD_HOST}`) return PROD_EDGE;
  }
  return "";
}

export const EDGE_API_BASE = resolveEdgeBase();

// ── Breaker v2 — per policyKey ────────────────────────────────────────────
// policyKey do call site khai báo tường minh (config.policyKey) khớp tên
// EDGE_POLICIES backend. Path tĩnh không truyền key → dùng chính path (đã
// canonical). Path động (UUID/slug) BẮT BUỘC có policyKey — dev warn.
const _breakers = new Map(); // policyKey -> { blockedUntil, probing }
const _BREAKER_CAP = 60;

// Nhóm ESSENTIAL: tối thiểu để dựng trang — CHỈ tournament core được
// cold-fallback origin có budget khi edge chết và không còn stale.
// Branding/feature-flags/display-settings có default + last-known localStorage
// phía FE; regulation không bắt buộc để dựng trang → tất cả đi nhánh stale/degraded.
// v17.05 (08/2026) — Livestream data phải luôn hiện player. Khi edge 5xx và
// không còn stale → fallback origin có budget → tránh màn "System busy" trắng
// trong lúc user đang xem live. Các key này khớp EDGE_POLICIES backend.
const ESSENTIAL_FALLBACK = new Set([
  "t_detail",
  "ls_tournament",
  "livestream_tournament_sessions",
  "live_match_ids",
  "m_detail",
]);
const _ESSENTIAL_ORIGIN_GAP_MS = 20_000; // budget: ≥20s giữa 2 lần fallback/key
const _essentialOriginLastAt = new Map(); // policyKey -> ts

// lastGood TTL — 3 nhóm đơn giản (tĩnh 30' / bán tĩnh 5' / realtime 45s).
const _STATIC_KEYS = new Set([
  "branding", "t_regulation", "vn_provinces", "vn_wards",
  "elo_system_chart", "chat_templates",
]);
const _REALTIME_KEYS = new Set([
  "m_list", "m_detail", "m_batch", "t_snapshot", "t_timeline",
  "ls_tournament", "live_match_ids", "livestream_tournament_sessions",
  "match_embeds_tournament", "chat_messages", "live_feed",
  "t_bracket_live", "t_featured_match", "t_header_stats",
]);
const _STALE_STATIC_MS = 30 * 60_000;
const _STALE_SEMI_MS = 5 * 60_000;
const _STALE_REALTIME_MS = 45_000;
const _STALE_HARD_CAP_MS = 30 * 60_000; // trần tuyệt đối khi origin budget chặn

function _staleTtlFor(pk) {
  if (_STATIC_KEYS.has(pk)) return _STALE_STATIC_MS;
  if (_REALTIME_KEYS.has(pk)) return _STALE_REALTIME_MS;
  return _STALE_SEMI_MS;
}

// ── Telemetry (bounded) ───────────────────────────────────────────────────
const _stats = {
  edge_ok: 0, edge_abort: 0, edge_429: 0, edge_4xx: 0, edge_fail: 0,
  breaker_open: 0, breaker_probe: 0, stale_served: 0,
  origin_fallback: 0, origin_essential: 0, degraded: 0,
  snapshot_served: 0, snapshot_saved: 0,
};
const _statsByKey = new Map(); // policyKey -> { edge_fail, degraded, origin_essential }
function _bumpKey(pk, field) {
  if (!_statsByKey.has(pk) && _statsByKey.size >= 40) return;
  const row = _statsByKey.get(pk) || { edge_fail: 0, degraded: 0, origin_essential: 0 };
  row[field] = (row[field] || 0) + 1;
  _statsByKey.set(pk, row);
}

export function getEdgeStats() {
  return {
    ..._stats,
    by_key: Object.fromEntries(_statsByKey),
    breakers_open: [..._breakers.entries()]
      .filter(([, b]) => Date.now() < b.blockedUntil)
      .map(([k]) => k),
  };
}

if (typeof window !== "undefined") {
  try {
    const dbg = process.env.NODE_ENV === "development"
      || window.localStorage?.getItem("edge_debug") === "1";
    if (dbg) window.__plEdgeStats = getEdgeStats;
  } catch { /* noop */ }
}

// ── PR-C — Single-flight coalescing (path+params) ─────────────────────────
const _inflight = new Map();

function _coKey(path, config) {
  try {
    return `${path}|${JSON.stringify(config?.params || {})}`;
  } catch {
    return path;
  }
}

// ── PR-D — Origin fallback storm protection (giữ nguyên trạng) ────────────
const _lastGood = new Map(); // coKey -> { res, at, pk }
const _LASTGOOD_CAP = 150;
let _originDownUntil = 0;

function _rememberGood(key, res, pk) {
  try { governor.reportRequestOk(); } catch { /* noop */ }
  if (_lastGood.size >= _LASTGOOD_CAP) {
    _lastGood.delete(_lastGood.keys().next().value);
  }
  _lastGood.set(key, { res, at: Date.now(), pk });
  // Đợt Offline-3 — persist snapshot IndexedDB (CHỈ policy public-anonymous
  // trong registry, fire-and-forget, debounce/LRU/size guard bên trong).
  if (snapshots.isPersistable(pk)) {
    snapshots.persistSnapshot(key, pk, res)
      .then((saved) => { if (saved) _stats.snapshot_saved += 1; })
      .catch(() => { /* noop — private mode / IDB unavailable */ });
  }
}

// Báo UI (OfflineBanner) đang serve dữ liệu cũ + thời điểm lưu. Throttle 2s.
let _lastOfflineDataEventAt = 0;
function _announceOfflineData(savedAt) {
  try {
    if (Date.now() - _lastOfflineDataEventAt < 2_000) return;
    _lastOfflineDataEventAt = Date.now();
    window.dispatchEvent(new CustomEvent("pl:offline-data-served", { detail: { savedAt } }));
  } catch { /* noop */ }
}

function _staleFor(key, pk) {
  const hit = _lastGood.get(key);
  if (!hit) return null;
  return Date.now() - hit.at <= _staleTtlFor(pk) ? hit.res : null;
}

function _staleAnyFor(key) {
  const hit = _lastGood.get(key);
  return hit && Date.now() - hit.at <= _STALE_HARD_CAP_MS ? hit.res : null;
}

function _retryAfterMs(err) {
  const ra = parseInt(err?.response?.headers?.["retry-after"], 10);
  if (Number.isFinite(ra) && ra > 0) return Math.min(ra, 120) * 1000;
  return (20 + Math.random() * 20) * 1000;
}

// PR-K v2 UX — báo UI (toast nhẹ qua serverBusyNotice) khi degraded. Throttle 30s.
let _lastBusyEventAt = 0;
function _notifyBusy(retryAfterMs) {
  try {
    if (Date.now() - _lastBusyEventAt < 30_000) return;
    _lastBusyEventAt = Date.now();
    window.dispatchEvent(new CustomEvent("pl:server-busy", { detail: { retryAfterMs } }));
  } catch { /* noop */ }
}

function _isAbort(err, config) {
  return (
    err?.code === "ERR_CANCELED"
    || err?.name === "CanceledError"
    || err?.name === "AbortError"
    || (typeof axios.isCancel === "function" && axios.isCancel(err))
    || Boolean(config?.signal?.aborted)
  );
}

function _isShedError(err) {
  if (err?.code === "ERR_CANCELED") return false;
  const st = err?.response?.status;
  if (!err?.response) return true; // network error thật
  return st === 429 || st === 502 || st === 503 || st === 504;
}

// Chỉ dùng cho unit test — reset state module-level.
export function _resetEdgeStateForTest() {
  _originDownUntil = 0;
  _inflight.clear();
  _lastGood.clear();
  _breakers.clear();
  _essentialOriginLastAt.clear();
  _statsByKey.clear();
  Object.keys(_stats).forEach((k) => { _stats[k] = 0; });
}

// KHÔNG withCredentials + KHÔNG header custom → simple CORS request,
// Worker cache an toàn (không lo cache nhầm dữ liệu cá nhân).
const plain = axios.create({ timeout: 15000 });

export function edgeAvailable() {
  return Boolean(EDGE_API_BASE);
}

// v17.03 — Log verdict + bypass-reason từ Worker. Bật bằng
// localStorage.setItem('edge_debug','1'); mặc định ON trong dev.
function _edgeLog(kind, path, res, extra) {
  try {
    const dbg =
      process.env.NODE_ENV === "development" ||
      (typeof window !== "undefined" && window.localStorage?.getItem("edge_debug") === "1");
    if (!dbg) return;
    const verdict = res?.headers?.["x-edge-cache"] || res?.headers?.get?.("x-edge-cache");
    const reason = res?.headers?.["x-edge-bypass-reason"] || res?.headers?.get?.("x-edge-bypass-reason");
    const st = res?.status || res?.response?.status;
    // eslint-disable-next-line no-console
    console.info(
      `[edgeApi] ${kind} path=${path} status=${st ?? "?"} verdict=${verdict || "-"} reason=${reason || "-"}${extra ? ` ${extra}` : ""}`,
    );
  } catch { /* noop */ }
}

// v16.96 scale-1000 — jitter chu kỳ poll (±20%) chống N client bắn cùng nhịp.
export function jitter(ms) {
  return Math.round(ms * (0.8 + Math.random() * 0.4));
}

const _DYNAMIC_SEG_RE = /\/[0-9a-f]{8}-[0-9a-f]{4}|\/\d{4,}/i;

function _resolvePolicyKey(path, config) {
  if (config?.policyKey) return config.policyKey;
  const clean = path.split("?")[0];
  if (process.env.NODE_ENV === "development" && _DYNAMIC_SEG_RE.test(clean)) {
    // eslint-disable-next-line no-console
    console.warn(`[edgeApi] path động "${clean}" thiếu policyKey — breaker sẽ key theo raw URL. Thêm { policyKey } khớp EDGE_POLICIES.`);
  }
  return clean;
}

function _openBreaker(pk) {
  if (_breakers.size >= _BREAKER_CAP && !_breakers.has(pk)) {
    _breakers.delete(_breakers.keys().next().value);
  }
  _breakers.set(pk, {
    blockedUntil: Date.now() + (4 + Math.random() * 3) * 60 * 1000,
    probing: false,
  });
}

// Trả true nếu request này KHÔNG được đi edge (breaker mở / probe khác đang bay).
// Nếu breaker hết hạn cooldown → caller hiện tại trở thành probe duy nhất.
function _edgeBlocked(pk) {
  const b = _breakers.get(pk);
  if (!b) return false;
  if (Date.now() < b.blockedUntil) return true;
  if (b.probing) return true; // đã có probe đang bay — chờ kết quả
  b.probing = true;
  _stats.breaker_probe += 1;
  return false;
}

export async function edgeGet(path, config = {}) {
  if (process.env.NODE_ENV === "development") {
    const known = path.startsWith("/public/") || path === "/branding" || path === "/feature-flags";
    if (!known) {
      // eslint-disable-next-line no-console
      console.warn(`[edgeApi] path "${path}" ngoài nhóm public đã audit — kiểm tra EDGE_POLICIES trước khi dùng edgeGet.`);
    }
  }
  const key = _coKey(path, config);
  // PR-C — coalesce: skip khi caller truyền AbortSignal riêng.
  if (!config.signal) {
    const pending = _inflight.get(key);
    if (pending) return pending;
    const p = _edgeGetInner(path, config, key);
    _inflight.set(key, p);
    const cleanup = () => _inflight.delete(key);
    p.then(cleanup, cleanup);
    return p;
  }
  return _edgeGetInner(path, config, key);
}

async function _edgeGetInner(path, config, coKey) {
  const pk = _resolvePolicyKey(path, config);
  // Đợt Offline-1 — confirmed offline: lastGood/degraded NGAY, 0 network,
  // không mở breaker, không probe, không fallback origin.
  if (governor.isOffline()) {
    const hit = _lastGood.get(coKey);
    if (hit && Date.now() - hit.at <= _STALE_HARD_CAP_MS) {
      _stats.stale_served += 1;
      _announceOfflineData(hit.at);
      return hit.res;
    }
    // Đợt Offline-3 — memory miss → thử snapshot IndexedDB (0 network).
    const snap = await snapshots.readSnapshot(coKey).catch(() => null);
    if (snap) {
      _stats.snapshot_served += 1;
      _announceOfflineData(snap.savedAt);
      return {
        data: snap.data,
        status: snap.status || 200,
        headers: {},
        plSnapshot: { source: "snapshot", savedAt: snap.savedAt, isStale: true },
      };
    }
    const err = new Error(`offline (${pk})`);
    err.isOffline = true;
    err.isEdgeDegraded = true;
    throw err;
  }
  const axCfg = { ...config };
  delete axCfg.policyKey;

  if (!EDGE_API_BASE) {
    // Preview / không có edge — origin trực tiếp, PR-D nguyên trạng.
    return _originGet(path, axCfg, coKey, pk, false);
  }

  if (!_edgeBlocked(pk)) {
    const breakerEntry = _breakers.get(pk); // != null ⇒ request này là probe
    try {
      const r = await plain.get(`${EDGE_API_BASE}/api${path}`, axCfg);
      _stats.edge_ok += 1;
      if (breakerEntry) _breakers.delete(pk); // probe OK → đóng breaker
      _edgeLog("edge", path, r);
      _rememberGood(coKey, r, pk);
      return r;
    } catch (e) {
      // 1) Abort do FE chủ động (đổi route/React Query) — KHÔNG phải lỗi edge.
      if (_isAbort(e, config)) {
        _stats.edge_abort += 1;
        if (breakerEntry) breakerEntry.probing = false;
        throw e;
      }
      const st = e?.response?.status;
      // 2) 429 — origin đang load-shed (Worker pass-through): tôn trọng,
      //    KHÔNG mở breaker edge, KHÔNG dồn thêm origin.
      if (st === 429) {
        _stats.edge_429 += 1;
        if (breakerEntry) breakerEntry.probing = false;
        throw e;
      }
      // 3) 4xx data thật (404 slug sai...) — edge sống, trả nguyên.
      if (st && st < 500) {
        _stats.edge_4xx += 1;
        if (breakerEntry) _breakers.delete(pk); // edge phản hồi được → đóng
        throw e;
      }
      // 4) Network/timeout/5xx thật → mở breaker CHO policyKey này.
      _stats.edge_fail += 1;
      _bumpKey(pk, "edge_fail");
      _openBreaker(pk);
      _edgeLog("edge_fail", path, e?.response || {}, `pk=${pk} err=${e?.message || "?"}`);
      // rơi xuống degraded path bên dưới
    }
  } else {
    _stats.breaker_open += 1;
  }

  // ── Degraded path: breaker mở hoặc edge vừa fail ─────────────────────
  const stale = _staleFor(coKey, pk);
  if (stale) {
    _stats.stale_served += 1;
    _edgeLog("stale", path, stale, `pk=${pk}`);
    return stale;
  }
  if (!ESSENTIAL_FALLBACK.has(pk)) {
    // Heavy/realtime: KHÔNG fallback origin — UI giữ khung + tự retry theo poll.
    _stats.degraded += 1;
    _bumpKey(pk, "degraded");
    _notifyBusy(30_000);
    const err = new Error(`edge degraded (${pk}) — giữ dữ liệu cũ, sẽ tự thử lại`);
    err.isEdgeDegraded = true;
    throw err;
  }
  // Essential: cold-fallback origin có budget (≥20s/key) + single-flight
  // (PR-C coalesce theo path+params đã gom caller trùng URL).
  const lastAt = _essentialOriginLastAt.get(pk) || 0;
  if (Date.now() - lastAt < _ESSENTIAL_ORIGIN_GAP_MS) {
    const any = _staleAnyFor(coKey);
    if (any) {
      _stats.stale_served += 1;
      return any;
    }
    _stats.degraded += 1;
    const err = new Error(`origin budget (${pk}) — thử lại sau`);
    err.isEdgeDegraded = true;
    throw err;
  }
  _essentialOriginLastAt.set(pk, Date.now());
  _stats.origin_essential += 1;
  _bumpKey(pk, "origin_essential");
  return _originGet(path, axCfg, coKey, pk, true);
}

async function _originGet(path, axCfg, coKey, pk, viaEssentialFallback) {
  // PR-D — origin breaker đang mở → stale hoặc fail-fast (0 network).
  if (Date.now() < _originDownUntil) {
    const stale = _staleFor(coKey, pk);
    if (stale) {
      _edgeLog("origin_breaker_stale", path, stale);
      _notifyBusy(_originDownUntil - Date.now());
      return stale;
    }
    const err = new Error("origin đang cooldown (PR-D breaker) — thử lại sau");
    err.isOriginBreaker = true;
    throw err;
  }
  try {
    const r2 = await plain.get(`${API}${path}`, axCfg);
    _stats.origin_fallback += 1;
    _edgeLog(viaEssentialFallback ? "origin_essential" : "origin_fallback", path, r2);
    _rememberGood(coKey, r2, pk);
    return r2;
  } catch (e2) {
    // PR-D — origin shed (429/502/503/504/network) → mở breaker theo
    // Retry-After; 5xx bất kỳ → cố serve stale để UI không vỡ.
    if (_isShedError(e2)) {
      const cooldown = _retryAfterMs(e2);
      _originDownUntil = Date.now() + cooldown;
      _notifyBusy(cooldown);
    }
    const st2 = e2?.response?.status;
    if (!e2?.response || st2 >= 500 || st2 === 429) {
      if (e2?.code !== "ERR_CANCELED") {
        const stale = _staleFor(coKey, pk);
        if (stale) {
          _edgeLog("origin_fail_stale", path, stale, `err=${e2?.message || "?"}`);
          return stale;
        }
      }
    }
    throw e2;
  }
}
